import { UploadCloud } from 'lucide-react';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';

import type { DataExplorerUploadSurfaceProps } from './types';
import { cn } from './utils';

export function DataExplorerUploadSurface({
  accept,
  multiple = false,
  disabled = false,
  busy = false,
  title = 'Upload files',
  description,
  hint,
  busyLabel = 'Uploading...',
  icon,
  inputProps,
  className,
  classNames,
  onFilesSelected,
}: DataExplorerUploadSurfaceProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const emitFiles = (files: FileList | File[]) => {
    if (disabled || busy) return;
    const selected = Array.from(files);
    if (selected.length > 0) onFilesSelected?.(selected);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    emitFiles(event.dataTransfer.files);
  };

  const renderText = (value: ReactNode, fallbackClassName: string, slot?: string) =>
    value ? <span data-slot={slot} className={cn('max-w-full [overflow-wrap:anywhere]', fallbackClassName)}>{value}</span> : null;

  return (
    <div
      data-monkeys-component="data-explorer-upload-surface"
      data-state={busy ? 'busy' : isDragging ? 'dragging' : 'idle'}
      className={cn(
        'rounded-[var(--radius)] border border-dashed border-border bg-muted/20 p-6 text-center transition-colors',
        isDragging && 'border-primary/60 bg-primary/5',
        (disabled || busy) && 'pointer-events-none opacity-60',
        classNames?.root,
        className,
      )}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled && !busy) setIsDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        event.preventDefault();
        if (event.currentTarget === event.target) setIsDragging(false);
      }}
      onDrop={handleDrop}
    >
      <input
        {...inputProps}
        ref={inputRef}
        className="sr-only"
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled || busy}
        onChange={(event) => {
          emitFiles(event.target.files ?? []);
          event.target.value = '';
        }}
      />
      <button
        type="button"
        className={cn('mx-auto flex min-w-0 flex-col items-center gap-2 text-center', classNames?.button)}
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
      >
        <span className={cn('text-muted-foreground', classNames?.icon)}>
          {busy ? <span className="inline-block h-7 w-7 animate-spin rounded-full border-2 border-primary/25 border-t-primary" aria-hidden="true" /> : icon ?? <UploadCloud className="h-7 w-7" aria-hidden="true" />}
        </span>
        {renderText(busy ? busyLabel : title, 'text-sm font-semibold text-foreground', 'title')}
        {renderText(description, 'max-w-xl text-xs leading-5 text-muted-foreground', 'description')}
        {renderText(hint, 'max-w-xl text-xs leading-5 text-muted-foreground', 'hint')}
      </button>
    </div>
  );
}
